/**
 * Naming rules for the facility studio (z8nrz7fw25): what a new floor, room or
 * building is called before anyone renames it. Pure, so "the next floor up is
 * the 3rd Floor" is a tested rule rather than a guess inside a click handler.
 */

const ORDINAL_SUFFIX: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' }
const ordinals = new Intl.PluralRules('en-US', { type: 'ordinal' })

/** 2 → "2nd Floor", 0 → "Ground Floor", -1 → "Basement 1". */
export function floorName(level: number): string {
  if (level === 0) return 'Ground Floor'
  if (level < 0) return `Basement ${-level}`
  return `${level}${ORDINAL_SUFFIX[ordinals.select(level)]} Floor`
}

/** The storey above the highest one, or the 1st when there are none. */
export function nextFloorUp(levels: number[]): { level: number; name: string } {
  const level = levels.length === 0 ? 1 : Math.max(...levels) + 1
  return { level, name: floorName(level) }
}

/**
 * "Room N" for the lowest N not already used, across the whole site: room
 * labels are equipment IDs and so unique per tenant, and a gap left by a
 * deleted room is reused rather than counting past it.
 */
export function nextRoomLabel(taken: string[]): string {
  const used = new Set(taken.map((t) => t.trim().toLowerCase()))
  for (let n = 1; ; n++) {
    if (!used.has(`room ${n}`)) return `Room ${n}`
  }
}

/**
 * The example a facility form shows, made from the tenant's own name so it
 * reads as theirs: "Children's Hospital" → code "CH", name "Children's
 * Hospital". A generic example when there is no tenant name.
 */
export function facilityExample(tenantName: string | null | undefined): { code: string; name: string } {
  const name = (tenantName ?? '').trim()
  if (!name) return { code: 'MAIN', name: 'Main campus' }
  const words = name.split(/[\s\-–—/]+/).filter((w) => /[A-Za-z0-9]/.test(w))
  const code =
    words.length > 1
      ? words.map((w) => w.replace(/[^A-Za-z0-9]/g, '')[0] ?? '').join('')
      : (words[0] ?? name).replace(/[^A-Za-z0-9]/g, '').slice(0, 4)
  return { code: code.toUpperCase().slice(0, 6) || 'MAIN', name }
}

/** A building's short badge from its name: "Building 2" → "2", "North Wing" → "NW". */
export function buildingCodeFrom(name: string, taken: string[]): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const number = words.find((w) => /^\d+$/.test(w))
  const base = (
    number ??
    (words.length > 1 ? words.map((w) => w[0]).join('') : (words[0] ?? 'B').slice(0, 3))
  )
    .toUpperCase()
    .slice(0, 6)
  const used = new Set(taken.map((t) => t.toUpperCase()))
  if (!used.has(base)) return base
  for (let n = 2; ; n++) {
    const candidate = `${base.slice(0, 5)}${n}`
    if (!used.has(candidate)) return candidate
  }
}
